import { LitElement, html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { navigateTo } from '../services/router.js';
import * as cockpitApi from '../services/cockpit-api.js';
import * as inboxApi from '../services/founder-inbox-api.js';
import type { CockpitOverview } from '../services/cockpit-api.js';
import type { InboxAction } from '../services/founder-inbox-api.js';

/**
 * Jarvis-Cockpit — der Startscreen von Compass. Ein immersives HUD über echten Compass-Daten:
 * Freigaben (Founder-Inbox), Signale, Decisions, Event-Durchsatz, Firmen/Agents, Trust.
 * Die Optik ist Iron-Man-HUD; die Inhalte sind real (eine Aggregation: /api/cockpit/overview).
 *
 * Light-DOM-Komponente: alle Styles unter `.jv` gescoped, damit nichts ins App-Shell leakt.
 * Canvas-Animationen (Orb-Partikel, Radar, Sparkline) laufen über RAF/Intervalle, die in
 * disconnectedCallback sauber abgeräumt werden.
 */
@customElement('cp-page-jarvis')
export class CpPageJarvis extends LitElement {
  createRenderRoot() { return this; }

  @state() private _ov: CockpitOverview | null = null;
  @state() private _error = '';
  @state() private _now = new Date();
  @state() private _busy = '';

  private _raf = 0;
  private _timers: number[] = [];
  private _pT = 0;
  private _rT = 0;
  private _particles: Array<{ r: number; theta: number; phi: number; size: number; seed: number; twinkle: number }> = [];
  private _spark: number[] = [];

  override connectedCallback(): void {
    super.connectedCallback();
    this._initParticles();
    void this._load();
    this._timers.push(window.setInterval(() => { this._now = new Date(); }, 1000));
    this._timers.push(window.setInterval(() => void this._load(), 15000));
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    cancelAnimationFrame(this._raf);
    this._timers.forEach((t) => clearInterval(t));
    this._timers = [];
  }

  override firstUpdated(): void {
    this._buildOrbSvg();
    this._startAnimations();
  }

  private async _load(): Promise<void> {
    try {
      this._ov = await cockpitApi.getOverview();
      this._error = '';
      // Sparkline aus echtem Event-Durchsatz speisen (24 1h-Buckets).
      if (this._ov.events.throughput?.length) {
        this._spark = this._ov.events.throughput.map((n) => n);
      }
    } catch (e) {
      this._error = e instanceof Error ? e.message : 'Cockpit nicht erreichbar';
    }
  }

  // ---------- Approvals (inline, direkt aus dem Cockpit) ----------
  private async _approve(actionId: string): Promise<void> {
    this._busy = actionId;
    try { await inboxApi.approveAction(actionId); await this._load(); }
    catch (e) { this._error = e instanceof Error ? e.message : 'Fehler'; }
    finally { this._busy = ''; }
  }
  private async _veto(actionId: string): Promise<void> {
    this._busy = actionId;
    try { await inboxApi.vetoAction(actionId, false); await this._load(); }
    catch (e) { this._error = e instanceof Error ? e.message : 'Fehler'; }
    finally { this._busy = ''; }
  }

  private _actionKindLabel(kind: string): string {
    return kind === 'send_ack_email' ? 'Bestätigungs-Mail'
      : kind === 'applai_project_upload' ? 'Applai-Upload'
      : kind === 'playbook_approval' ? 'Playbook-Freigabe' : kind;
  }

  /** Human-readable line for the cockpit banner: the action's summary, else its id. */
  private _actionSummary(a: InboxAction): string {
    const s = (a.payload as Record<string, unknown>)?.summary;
    return typeof s === 'string' && s.trim() ? s : (a.strategyId ?? a.actionId);
  }

  // ---------- Orb SVG (Ticks + Datapoints) ----------
  private _buildOrbSvg(): void {
    const ticks = this.querySelector('#jvOuterTicks');
    if (ticks && !ticks.childNodes.length) {
      const frag = document.createDocumentFragment();
      for (let i = 0; i < 72; i++) {
        const a = (i / 72) * Math.PI * 2;
        const r1 = 246, r2 = i % 6 === 0 ? 232 : 240;
        const line = document.createElementNS('http://www.w3.org/2000/svg', 'line');
        line.setAttribute('x1', String(Math.cos(a) * r1));
        line.setAttribute('y1', String(Math.sin(a) * r1));
        line.setAttribute('x2', String(Math.cos(a) * r2));
        line.setAttribute('y2', String(Math.sin(a) * r2));
        line.setAttribute('stroke', '#6ce0ff');
        line.setAttribute('stroke-opacity', i % 6 === 0 ? '0.9' : '0.4');
        line.setAttribute('stroke-width', i % 6 === 0 ? '1.2' : '0.5');
        frag.appendChild(line);
      }
      ticks.appendChild(frag);
    }
    const dp = this.querySelector('#jvDataPoints');
    if (dp && dp.childNodes.length <= 1) {
      const frag = document.createDocumentFragment();
      const n = 18;
      for (let i = 0; i < n; i++) {
        const a = (i / n) * Math.PI * 2;
        const r = 200 + (i % 5) * 2 - 4;
        const c = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
        c.setAttribute('cx', String(Math.cos(a) * r));
        c.setAttribute('cy', String(Math.sin(a) * r));
        c.setAttribute('r', '2.5');
        c.setAttribute('fill', '#aff1ff');
        c.setAttribute('opacity', '0.85');
        frag.appendChild(c);
      }
      dp.appendChild(frag);
    }
  }

  private _initParticles(): void {
    const N = 320;
    this._particles = [];
    for (let i = 0; i < N; i++) {
      this._particles.push({
        r: Math.pow((i % 50) / 50, 1.5) * 0.42,
        theta: (i / N) * Math.PI * 2,
        phi: Math.acos(((i % 31) / 31) * 2 - 1),
        size: ((i % 7) / 7) * 1.6 + 0.4,
        seed: (i % 100),
        twinkle: (i % 17) / 17 * Math.PI * 2,
      });
    }
    this._spark = new Array(24).fill(0).map((_, i) => 20 + (i % 7) * 4);
  }

  private _startAnimations(): void {
    const pCanvas = this.querySelector('#jvParticles') as HTMLCanvasElement | null;
    const rCanvas = this.querySelector('#jvRadar') as HTMLCanvasElement | null;
    const sCanvas = this.querySelector('#jvSpark') as HTMLCanvasElement | null;
    const pCtx = pCanvas?.getContext('2d') ?? null;
    const rCtx = rCanvas?.getContext('2d') ?? null;
    const sCtx = sCanvas?.getContext('2d') ?? null;

    const sizeCanvas = (cv: HTMLCanvasElement | null, ctx: CanvasRenderingContext2D | null) => {
      if (!cv || !ctx) return;
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const rect = cv.getBoundingClientRect();
      cv.width = Math.max(1, rect.width * dpr);
      cv.height = Math.max(1, rect.height * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    const loop = (): void => {
      sizeCanvas(pCanvas, pCtx); sizeCanvas(rCanvas, rCtx); sizeCanvas(sCanvas, sCtx);
      if (pCtx && pCanvas) this._drawParticles(pCtx, pCanvas);
      if (rCtx && rCanvas) this._drawRadar(rCtx, rCanvas);
      if (sCtx && sCanvas) this._drawSpark(sCtx, sCanvas);
      this._raf = requestAnimationFrame(loop);
    };
    this._raf = requestAnimationFrame(loop);
  }

  private _drawParticles(ctx: CanvasRenderingContext2D, cv: HTMLCanvasElement): void {
    this._pT += 0.005;
    const w = cv.width / (window.devicePixelRatio || 1);
    const h = cv.height / (window.devicePixelRatio || 1);
    ctx.clearRect(0, 0, w, h);
    const cx = w / 2, cy = h / 2;
    const baseR = Math.min(w, h) * 0.5;
    const grad = ctx.createRadialGradient(cx, cy, 0, cx, cy, baseR * 0.55);
    grad.addColorStop(0, 'rgba(108,224,255,0.18)');
    grad.addColorStop(0.4, 'rgba(108,224,255,0.05)');
    grad.addColorStop(1, 'rgba(108,224,255,0)');
    ctx.fillStyle = grad; ctx.fillRect(0, 0, w, h);
    for (const p of this._particles) {
      const ang = p.theta + this._pT * (0.6 + p.seed * 0.01);
      const rr = p.r * baseR;
      const tilt = Math.sin(p.phi);
      const x = cx + Math.cos(ang) * rr;
      const y = cy + Math.sin(ang) * rr * (0.85 + tilt * 0.15);
      const tw = 0.65 + 0.35 * Math.sin(this._pT * 6 + p.twinkle);
      const fade = 1 - p.r;
      const alpha = Math.min(1, fade * tw * 1.2);
      ctx.beginPath();
      ctx.arc(x, y, p.size, 0, Math.PI * 2);
      ctx.fillStyle = `rgba(${Math.round(175 + 80 * fade)},241,255,${alpha})`;
      ctx.shadowColor = 'rgba(108,224,255,0.95)';
      ctx.shadowBlur = 6 * fade;
      ctx.fill();
    }
    ctx.shadowBlur = 0;
  }

  private _drawRadar(ctx: CanvasRenderingContext2D, cv: HTMLCanvasElement): void {
    this._rT += 0.025;
    const w = cv.width / (window.devicePixelRatio || 1);
    const h = cv.height / (window.devicePixelRatio || 1);
    ctx.clearRect(0, 0, w, h);
    const cx = w / 2, cy = h / 2;
    const R = Math.min(w, h) * 0.45;
    const grad = ctx.createRadialGradient(cx, cy, 0, cx, cy, R);
    grad.addColorStop(0, 'rgba(26,127,212,0.18)');
    grad.addColorStop(1, 'rgba(2,19,42,0)');
    ctx.fillStyle = grad; ctx.beginPath(); ctx.arc(cx, cy, R, 0, Math.PI * 2); ctx.fill();
    ctx.strokeStyle = 'rgba(108,224,255,0.4)'; ctx.lineWidth = 0.8;
    for (let i = 1; i <= 3; i++) { ctx.beginPath(); ctx.arc(cx, cy, R * i / 3, 0, Math.PI * 2); ctx.stroke(); }
    ctx.beginPath();
    ctx.moveTo(cx - R, cy); ctx.lineTo(cx + R, cy);
    ctx.moveTo(cx, cy - R); ctx.lineTo(cx, cy + R);
    ctx.strokeStyle = 'rgba(108,224,255,0.25)'; ctx.stroke();
    const sweepA = this._rT % (Math.PI * 2);
    ctx.beginPath(); ctx.moveTo(cx, cy);
    ctx.lineTo(cx + Math.cos(sweepA) * R, cy + Math.sin(sweepA) * R);
    ctx.strokeStyle = '#aff1ff'; ctx.lineWidth = 1.2; ctx.shadowColor = '#6ce0ff'; ctx.shadowBlur = 8; ctx.stroke();
    ctx.shadowBlur = 0;
    // Contacts = Agents (deterministisch verteilt)
    const n = Math.min(this._ov?.agents ?? 0, 16);
    for (let i = 0; i < n; i++) {
      const a = (i / Math.max(n, 1)) * Math.PI * 2 + 0.3;
      const r = 0.35 + ((i % 5) / 5) * 0.55;
      const x = cx + Math.cos(a) * r * R;
      const y = cy + Math.sin(a) * r * R;
      const diff = ((sweepA - a) + Math.PI * 2) % (Math.PI * 2);
      const lit = Math.max(0, 1 - diff / 0.9);
      ctx.beginPath();
      ctx.arc(x, y, 2.4 + lit * 1.2, 0, Math.PI * 2);
      ctx.fillStyle = `rgba(108,${Math.round(200 + lit * 55)},255,${0.5 + lit * 0.5})`;
      ctx.fill();
    }
  }

  private _drawSpark(ctx: CanvasRenderingContext2D, cv: HTMLCanvasElement): void {
    const w = cv.width / (window.devicePixelRatio || 1);
    const h = cv.height / (window.devicePixelRatio || 1);
    ctx.clearRect(0, 0, w, h);
    ctx.strokeStyle = 'rgba(108,224,255,0.10)'; ctx.lineWidth = 1;
    for (let i = 1; i < 4; i++) { const y = (h / 4) * i; ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(w, y); ctx.stroke(); }
    const data = this._spark.length ? this._spark : [0, 0];
    const max = Math.max(...data, 1) * 1.15;
    const min = 0;
    const xAt = (i: number) => (i / (data.length - 1)) * w;
    const yAt = (v: number) => h - ((v - min) / (max - min || 1)) * h * 0.85 - 4;
    ctx.beginPath();
    data.forEach((v, i) => { const x = xAt(i), y = yAt(v); i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y); });
    ctx.lineTo(w, h); ctx.lineTo(0, h); ctx.closePath();
    const fill = ctx.createLinearGradient(0, 0, 0, h);
    fill.addColorStop(0, 'rgba(108,224,255,0.45)');
    fill.addColorStop(1, 'rgba(108,224,255,0)');
    ctx.fillStyle = fill; ctx.fill();
    ctx.beginPath();
    data.forEach((v, i) => { const x = xAt(i), y = yAt(v); i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y); });
    ctx.strokeStyle = '#aff1ff'; ctx.lineWidth = 1.6; ctx.shadowColor = '#6ce0ff'; ctx.shadowBlur = 6; ctx.stroke();
    ctx.shadowBlur = 0;
  }

  // ---------- Helpers ----------
  private _pad(n: number): string { return String(n).padStart(2, '0'); }
  private _clock(): string { const d = this._now; return `${this._pad(d.getHours())}:${this._pad(d.getMinutes())}:${this._pad(d.getSeconds())}`; }
  private _dateStr(): string {
    const months = ['JAN','FEB','MAR','APR','MAY','JUN','JUL','AUG','SEP','OCT','NOV','DEC'];
    const d = this._now; return `${this._pad(d.getDate())} ${months[d.getMonth()]} ${d.getFullYear()}`;
  }
  private _shortTime(at: string): string {
    const t = at.includes('T') ? at.split('T')[1] : (at.split(' ')[1] ?? at);
    return (t ?? '').slice(0, 8);
  }
  private _eventClass(type: string): string {
    if (type.includes('failed') || type.includes('stalled')) return 'warn';
    if (type.includes('executed') || type.includes('approved') || type.includes('created')) return 'ok';
    return 'info';
  }
  private _maxTrustLevel(): number {
    return this._ov?.trust.reduce((m, t) => Math.max(m, t.autonomyLevel), 0) ?? 0;
  }

  override render() {
    const ov = this._ov;
    const pending = ov?.approvals.pendingCount ?? 0;
    const topAction = ov?.approvals.pending?.[0];
    const routes = ov?.signals.routes ?? {};
    const recent = ov?.events.recent ?? [];

    return html`
      ${this._styles()}
      <div class="jv">
        <div class="jv-grid-bg"></div>

        <!-- TOP BAR -->
        <header class="jv-top">
          <div class="jv-logo">PELORUS · JARVIS</div>
          <nav class="jv-menu">
            <span @click=${() => navigateTo('/signals')}>SIGNALE</span>
            <span @click=${() => navigateTo('/decisions')}>DECISIONS</span>
            <span @click=${() => navigateTo('/approvals')}>FREIGABEN</span>
            <span @click=${() => navigateTo('/companies')}>FIRMEN</span>
          </nav>
          <div class="jv-spacer"></div>
          <div class="jv-date">${this._clock()} · ${this._dateStr()}</div>
          <button class="jv-ask" @click=${() => navigateTo('/chat')}>+ ASK JARVIS</button>
        </header>

        <!-- LEFT -->
        <aside class="jv-left">
          <div class="jv-banner">
            <div class="jv-banner-title">SYSTEM ONLINE</div>
            <div class="jv-banner-sub">${this._error ? this._error.toUpperCase() : 'ALL PROJECTIONS NOMINAL'}</div>
          </div>

          <div class="jv-panel">
            <div class="jv-ptitle">SYSTEM STATUS <span class="jv-dot"></span></div>
            <div class="jv-stats">
              ${this._stat('FREIGABEN OFFEN', String(pending), pending > 0 ? 'warn' : 'good')}
              ${this._stat('SIGNALE', String(ov?.signals.total ?? '—'))}
              ${this._stat('EVENTS', String(ov?.events.total ?? '—'))}
              ${this._stat('FIRMEN', String(ov?.companies ?? '—'))}
              ${this._stat('AGENTS', String(ov?.agents ?? '—'))}
              ${this._stat('TRUST-STUFE', String(this._maxTrustLevel()))}
              ${Object.entries(routes).map(([r, n]) => this._stat(`→ ${r}`, String(n)))}
            </div>
          </div>

          <div class="jv-panel jv-left-bottom">
            <div class="jv-ptitle">ACTIVITY <span class="jv-dot"></span></div>
            <div class="jv-log">
              ${recent.length === 0
                ? html`<div class="jv-logline">&gt; warte auf Events…</div>`
                : recent.map((e) => html`
                  <div class="jv-logline ${this._eventClass(e.type)}">
                    <span class="jv-ts">${this._shortTime(e.at)}</span>&gt; ${e.type}
                  </div>
                `)}
            </div>
          </div>
        </aside>

        <!-- CENTER -->
        <main class="jv-center">
          <div class="jv-center-title"><span class="jv-lbl">COCKPIT</span> · ${ov ? new Date(ov.generatedAt).toLocaleTimeString('de-DE') : '—'}</div>
          <div class="jv-clock-block">
            <div class="jv-clock">${this._clock()}</div>
            <div class="jv-cdate">${this._dateStr()}</div>
          </div>

          <div class="jv-orb-wrap">
            <div class="jv-orb">
              <canvas id="jvParticles"></canvas>
              <svg viewBox="-260 -260 520 520">
                <defs>
                  <radialGradient id="jvGlow" cx="0" cy="0" r="120" gradientUnits="userSpaceOnUse">
                    <stop offset="0%" stop-color="#ffffff" stop-opacity="1"/>
                    <stop offset="20%" stop-color="#6ce0ff" stop-opacity="0.9"/>
                    <stop offset="55%" stop-color="#1a7fd4" stop-opacity="0.4"/>
                    <stop offset="100%" stop-color="#1a7fd4" stop-opacity="0"/>
                  </radialGradient>
                  <linearGradient id="jvArc" x1="-1" y1="0" x2="1" y2="0">
                    <stop offset="0%" stop-color="#6ce0ff" stop-opacity="0"/>
                    <stop offset="50%" stop-color="#aff1ff" stop-opacity="1"/>
                    <stop offset="100%" stop-color="#6ce0ff" stop-opacity="0"/>
                  </linearGradient>
                </defs>
                <g class="jv-pulse"><circle r="180" fill="url(#jvGlow)"/></g>
                <g>
                  <animateTransform attributeName="transform" type="rotate" from="0 0 0" to="360 0 0" dur="90s" repeatCount="indefinite"/>
                  <circle r="250" fill="none" stroke="#6ce0ff" stroke-width="0.5" stroke-opacity="0.3"/>
                  <g id="jvOuterTicks"></g>
                </g>
                <g>
                  <animateTransform attributeName="transform" type="rotate" from="0 0 0" to="360 0 0" dur="48s" repeatCount="indefinite"/>
                  <ellipse rx="240" ry="170" fill="none" stroke="#6ce0ff" stroke-width="1.1" stroke-opacity="0.5"/>
                  <ellipse rx="240" ry="170" fill="none" stroke="#aff1ff" stroke-width="0.35" stroke-opacity="0.85" stroke-dasharray="2 8"/>
                </g>
                <g>
                  <animateTransform attributeName="transform" type="rotate" from="360 0 0" to="0 0 0" dur="38s" repeatCount="indefinite"/>
                  <ellipse rx="170" ry="230" fill="none" stroke="#6ce0ff" stroke-width="0.9" stroke-opacity="0.42"/>
                </g>
                <g>
                  <animateTransform attributeName="transform" type="rotate" from="360 0 0" to="0 0 0" dur="14s" repeatCount="indefinite"/>
                  <path d="M -150 0 A 150 150 0 0 1 150 0" fill="none" stroke="url(#jvArc)" stroke-width="1.3"/>
                </g>
                <g id="jvDataPoints">
                  <animateTransform attributeName="transform" type="rotate" from="0 0 0" to="360 0 0" dur="34s" repeatCount="indefinite"/>
                </g>
                <circle r="110" fill="none" stroke="#6ce0ff" stroke-width="0.6" stroke-opacity="0.4" stroke-dasharray="1 3"/>
                <circle r="60" fill="none" stroke="#6ce0ff" stroke-width="0.5" stroke-opacity="0.35"/>
              </svg>
              <div class="jv-core"></div>
            </div>
          </div>

          <!-- BOTTOM BAND: Founder-Inbox / Freigaben -->
          <div class="jv-center-bottom">
            <div class="jv-pe">
              <div>
                <div class="jv-pe-title">FOUNDER-INBOX · FREIGABEN</div>
                <div class="jv-pe-amount">${pending}<span class="jv-pe-small">offen</span></div>
              </div>
              ${topAction ? html`
                <div class="jv-pe-action">
                  <div class="jv-pe-action-kind">${this._actionKindLabel(topAction.kind)}</div>
                  <div class="jv-pe-action-meta">${this._actionSummary(topAction)}</div>
                </div>
                <div class="jv-pe-buttons">
                  <button class="jv-approve" ?disabled=${this._busy === topAction.actionId} @click=${() => void this._approve(topAction.actionId)}>✓ Freigeben</button>
                  <button class="jv-veto" ?disabled=${this._busy === topAction.actionId} @click=${() => void this._veto(topAction.actionId)}>✕</button>
                  <button class="jv-more" @click=${() => navigateTo('/approvals')}>Alle →</button>
                </div>
              ` : html`
                <div class="jv-pe-action"><div class="jv-pe-action-meta">Keine offenen Freigaben.</div></div>
                <div class="jv-pe-buttons"><button class="jv-more" @click=${() => navigateTo('/approvals')}>Inbox →</button></div>
              `}
            </div>
          </div>
        </main>

        <!-- RIGHT -->
        <aside class="jv-right">
          <div class="jv-panel">
            <div class="jv-ptitle">AGENTS / RADAR <span class="jv-dot"></span></div>
            <div class="jv-prox">
              <div class="jv-radar-wrap"><canvas id="jvRadar"></canvas></div>
              <div class="jv-radar-info">
                <div class="jv-lbl">AGENTS</div>
                <div class="jv-big">${this._pad(ov?.agents ?? 0)}</div>
                <div class="jv-lbl">STATUS</div>
                <div class="${pending > 0 ? 'jv-warn' : 'jv-ok'}">${pending > 0 ? 'FREIGABE OFFEN' : 'NOMINAL'}</div>
              </div>
            </div>
          </div>

          <div class="jv-panel jv-qio">
            <div class="jv-ptitle">EVENT-DURCHSATZ (24H) <span class="jv-dot"></span></div>
            <div class="jv-qio-chart"><canvas id="jvSpark"></canvas></div>
            <div class="jv-qio-meta"><span>-24H</span><span>JETZT</span></div>
          </div>

          <div class="jv-panel jv-right-bottom">
            <div class="jv-ptitle">DECISIONS / STREAMS <span class="jv-dot"></span></div>
            <div class="jv-log jv-log-sm">
              ${recent.filter((e) => e.type.startsWith('decision') || e.type.startsWith('signal') || e.type.startsWith('action')).slice(0, 16).length === 0
                ? html`<div class="jv-logline">&gt; keine Decisions/Signals</div>`
                : recent.filter((e) => e.type.startsWith('decision') || e.type.startsWith('signal') || e.type.startsWith('action')).slice(0, 16).map((e) => html`
                  <div class="jv-logline ${this._eventClass(e.type)}">
                    <span class="jv-ts">${this._shortTime(e.at)}</span>&gt; ${e.type}
                  </div>
                `)}
            </div>
          </div>
        </aside>
      </div>
    `;
  }

  private _stat(k: string, v: string, cls = '') {
    return html`<div class="jv-stat"><span class="jv-k">${k}</span><span class="jv-v ${cls}">${v}</span></div>`;
  }

  private _styles() {
    return html`<style>
      .jv {
        --jv-cyan: #6ce0ff; --jv-cyan-glow: #aff1ff; --jv-blue2: #4fb8ff; --jv-dim: #2a6090;
        --jv-line: rgba(108,224,255,0.22); --jv-white: #e6f8ff; --jv-warn: #ffaa3a; --jv-good: #6cffb8;
        position: relative; height: 100%; min-height: calc(100vh - 60px);
        display: grid; grid-template-rows: 40px 1fr; grid-template-columns: 270px 1fr 320px;
        grid-template-areas: "top top top" "left center right"; gap: 8px; padding: 12px 16px;
        background: radial-gradient(ellipse at 50% 40%, #0a2654 0%, #02132a 38%, #00060f 80%, #000204 100%);
        color: var(--jv-cyan); font-family: var(--font-display);
        letter-spacing: 0.03em; overflow: hidden; border-radius: 10px;
      }
      .jv-grid-bg { position: absolute; inset: 0; z-index: 0; pointer-events: none;
        background-image: linear-gradient(rgba(108,224,255,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(108,224,255,0.04) 1px, transparent 1px);
        background-size: 54px 54px; mask-image: radial-gradient(ellipse at 50% 50%, black 30%, transparent 82%); }
      .jv > * { position: relative; z-index: 1; }
      .jv-top { grid-area: top; display: flex; align-items: center; gap: 22px; padding: 0 12px;
        border-top: 1px solid var(--jv-line); border-bottom: 1px solid var(--jv-line);
        font-family: var(--font-mono); font-size: 11px; color: var(--jv-blue2);
        background: linear-gradient(to bottom, rgba(10,74,138,0.18), transparent); }
      .jv-logo { color: var(--jv-cyan-glow); font-weight: 700; letter-spacing: 0.28em; text-shadow: 0 0 8px var(--jv-cyan); }
      .jv-menu { display: flex; gap: 18px; opacity: 0.75; }
      .jv-menu span { cursor: pointer; transition: color .2s, text-shadow .2s; }
      .jv-menu span:hover { color: var(--jv-cyan-glow); text-shadow: 0 0 6px var(--jv-cyan); }
      .jv-spacer { flex: 1; }
      .jv-date { color: var(--jv-white); text-shadow: 0 0 6px var(--jv-cyan); font-variant-numeric: tabular-nums; }
      .jv-ask { border: 1px solid rgba(108,224,255,0.55); padding: 3px 12px; border-radius: 12px; color: var(--jv-cyan-glow);
        background: rgba(108,224,255,0.06); cursor: pointer; font-family: var(--font-display); font-weight: 600; letter-spacing: 0.12em; }
      .jv-ask:hover { background: rgba(108,224,255,0.18); box-shadow: 0 0 14px rgba(108,224,255,0.45); }
      .jv-left { grid-area: left; display: flex; flex-direction: column; gap: 10px; overflow: hidden; }
      .jv-right { grid-area: right; display: flex; flex-direction: column; gap: 10px; overflow: hidden; }
      .jv-banner { border: 1px solid var(--jv-line); padding: 8px 12px; position: relative;
        background: linear-gradient(to right, rgba(10,74,138,0.5), rgba(10,74,138,0.1)); }
      .jv-banner::before { content: ''; position: absolute; top: 0; left: 0; bottom: 0; width: 3px; background: var(--jv-cyan); box-shadow: 0 0 10px var(--jv-cyan); }
      .jv-banner-title { font-weight: 800; font-size: 13px; letter-spacing: 0.32em; color: var(--jv-cyan-glow); text-shadow: 0 0 10px var(--jv-cyan); }
      .jv-banner-sub { font-family: var(--font-mono); font-size: 9px; color: var(--jv-blue2); letter-spacing: 0.18em; margin-top: 2px; }
      .jv-panel { border: 1px solid var(--jv-line); padding: 10px 12px;
        background: linear-gradient(to bottom, rgba(10,74,138,0.10), rgba(10,74,138,0.02)); }
      .jv-ptitle { font-family: var(--font-mono); font-size: 10px; letter-spacing: 0.22em; color: var(--jv-cyan);
        margin-bottom: 8px; text-transform: uppercase; padding-bottom: 4px; border-bottom: 1px solid var(--jv-line); display: flex; justify-content: space-between; }
      .jv-dot { width: 6px; height: 6px; border-radius: 50%; background: var(--jv-cyan); box-shadow: 0 0 8px var(--jv-cyan); animation: jvBlink 1.6s ease-in-out infinite; }
      @keyframes jvBlink { 50% { opacity: 0.25; } }
      .jv-stats { font-family: var(--font-mono); font-size: 11px; max-height: 38vh; overflow: auto; }
      .jv-stat { display: flex; justify-content: space-between; padding: 2px 0; border-bottom: 1px dashed rgba(108,224,255,0.08); }
      .jv-k { color: var(--jv-blue2); }
      .jv-v { color: var(--jv-cyan-glow); font-weight: 500; }
      .jv-v.warn { color: var(--jv-warn); } .jv-v.good { color: var(--jv-good); }
      .jv-left-bottom, .jv-right-bottom { flex: 1; min-height: 0; display: flex; flex-direction: column; }
      .jv-log { font-family: var(--font-mono); font-size: 10px; color: var(--jv-blue2); overflow: auto; flex: 1; }
      .jv-log-sm { font-size: 9px; }
      .jv-logline { line-height: 1.55; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
      .jv-logline.warn { color: var(--jv-warn); } .jv-logline.ok { color: var(--jv-good); } .jv-logline.info { color: var(--jv-cyan-glow); }
      .jv-ts { color: var(--jv-dim); margin-right: 6px; }
      .jv-center { grid-area: center; position: relative; overflow: hidden; }
      .jv-center-title { position: absolute; top: 10px; left: 12px; font-family: var(--font-mono); font-size: 10px; color: var(--jv-blue2); letter-spacing: 0.22em; z-index: 5; }
      .jv-lbl { color: var(--jv-cyan); }
      .jv-clock-block { position: absolute; top: 6px; right: 12px; text-align: right; z-index: 5; }
      .jv-clock { font-weight: 700; font-size: 46px; letter-spacing: 0.04em; color: var(--jv-cyan-glow); line-height: 1; font-variant-numeric: tabular-nums;
        text-shadow: 0 0 12px var(--jv-cyan), 0 0 24px rgba(108,224,255,0.6); font-family: var(--font-display); }
      .jv-cdate { font-family: var(--font-mono); font-size: 10px; color: var(--jv-blue2); letter-spacing: 0.28em; margin-top: 4px; }
      .jv-orb-wrap { position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; }
      .jv-orb { position: relative; width: 460px; height: 460px; max-width: 78%; max-height: 72%; }
      .jv-orb svg, .jv-orb canvas { position: absolute; inset: 0; width: 100%; height: 100%; }
      .jv-pulse { animation: jvPulse 4.2s ease-in-out infinite; transform-origin: center; }
      @keyframes jvPulse { 0%,100% { opacity: 0.55; } 50% { opacity: 0.85; } }
      .jv-core { position: absolute; top: 50%; left: 50%; width: 70px; height: 70px; transform: translate(-50%,-50%); border-radius: 50%;
        background: radial-gradient(circle at center, #fff 0%, #d4f6ff 20%, #6ce0ff 45%, #1a7fd4 70%, transparent 100%);
        box-shadow: 0 0 40px var(--jv-cyan), 0 0 80px var(--jv-cyan), 0 0 160px rgba(108,224,255,0.7); animation: jvCore 3.5s ease-in-out infinite; z-index: 3; }
      @keyframes jvCore { 0%,100% { transform: translate(-50%,-50%) scale(1); opacity: 1; } 50% { transform: translate(-50%,-50%) scale(1.12); opacity: 0.85; } }
      .jv-center-bottom { position: absolute; bottom: 8px; left: 50%; transform: translateX(-50%); width: 92%; z-index: 5; }
      .jv-pe { border: 1px solid var(--jv-line); padding: 8px 14px; display: grid; grid-template-columns: auto 1fr auto; gap: 16px; align-items: center;
        background: linear-gradient(to bottom, rgba(10,74,138,0.28), rgba(10,74,138,0.05)); }
      .jv-pe-title { font-family: var(--font-mono); font-size: 9px; letter-spacing: 0.24em; color: var(--jv-blue2); margin-bottom: 4px; }
      .jv-pe-amount { font-weight: 700; font-size: 30px; color: var(--jv-cyan-glow); text-shadow: 0 0 10px var(--jv-cyan); line-height: 1; }
      .jv-pe-small { font-size: 13px; opacity: 0.7; margin-left: 8px; color: var(--jv-cyan); }
      .jv-pe-action-kind { color: var(--jv-cyan-glow); font-size: 14px; font-weight: 600; }
      .jv-pe-action-meta { color: var(--jv-blue2); font-family: var(--font-mono); font-size: 10px; margin-top: 2px; }
      .jv-pe-buttons { display: flex; gap: 6px; align-items: center; }
      .jv-approve { cursor: pointer; border: none; background: #1f7a3a; color: #d6ffe4; border-radius: 6px; padding: 6px 14px; font-weight: 700; }
      .jv-approve:hover { background: #2e9d4d; box-shadow: 0 0 12px rgba(46,157,77,0.6); }
      .jv-veto { cursor: pointer; border: 1px solid #ff4a6b; background: transparent; color: #ff6b86; border-radius: 6px; padding: 6px 10px; }
      .jv-more { cursor: pointer; border: 1px solid var(--jv-line); background: rgba(108,224,255,0.06); color: var(--jv-cyan-glow); border-radius: 6px; padding: 6px 10px; font-size: 11px; }
      .jv-approve:disabled, .jv-veto:disabled { opacity: 0.5; cursor: default; }
      .jv-prox { display: grid; grid-template-columns: 120px 1fr; gap: 12px; align-items: center; }
      .jv-radar-wrap { position: relative; width: 120px; height: 120px; }
      .jv-radar-wrap canvas { position: absolute; inset: 0; width: 100%; height: 100%; }
      .jv-radar-info { font-family: var(--font-mono); font-size: 10px; color: var(--jv-blue2); line-height: 1.6; }
      .jv-radar-info .jv-lbl { color: var(--jv-cyan); letter-spacing: 0.2em; }
      .jv-big { font-weight: 700; font-size: 20px; color: var(--jv-cyan-glow); text-shadow: 0 0 8px var(--jv-cyan); margin: 2px 0; }
      .jv-ok { color: var(--jv-good); } .jv-warn { color: var(--jv-warn); }
      .jv-qio { height: 150px; display: flex; flex-direction: column; }
      .jv-qio-chart { flex: 1; position: relative; }
      .jv-qio-chart canvas { position: absolute; inset: 0; width: 100%; height: 100%; }
      .jv-qio-meta { display: flex; justify-content: space-between; font-family: var(--font-mono); font-size: 9px; color: var(--jv-blue2); letter-spacing: 0.2em; margin-top: 4px; }
      @media (max-width: 1100px) {
        .jv { grid-template-columns: 1fr; grid-template-areas: "top" "center" "left" "right"; height: auto; overflow: visible; }
        .jv-center { min-height: 460px; }
      }
    </style>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cp-page-jarvis': CpPageJarvis;
  }
}
